import type {
  Experience,
  ExperienceSection,
  LowerItem,
  ManagementExperienceItem
} from '@/generated-api'
import type { SelectItem } from '@/interfaces/components/ISelectComponent'
import type { IProfileListItem } from '@/interfaces/IProfileListItem'
import { defineStore } from 'pinia'
import { useLocalizationStore } from '../general-stores/localization'
import { profileDropdown } from '@/application/helpers/profileHelper'

export const useProfileExperienceStore = defineStore('profileExperienceStore', () => {
  const localizationStore = useLocalizationStore()

  const experienceInitialized = ref<boolean>(false)
  const professionelTitle = ref<string>('')
  const profileSummary = ref<string>('')
  const personalProfile = ref<string>('')
  const experiences = ref()
  const educationLevels = ref<SelectItem[]>([])
  const educationLevel = ref<SelectItem | undefined>()
  const managementExperience = ref<IProfileListItem[]>([])
  const managementItem = ref<ManagementExperienceItem>()

  const initExperience = (item: ExperienceSection) => {
    professionelTitle.value = item.candidateTitle ?? ''
    profileSummary.value = item.summary ?? ''
    personalProfile.value = item.personalProfile ?? ''
    if (item.subsections != undefined) {
      experiences.value = [
        { type: 0, data: item.subsections[0].items },
        { type: 1, data: item.subsections[1].items },
        { type: 2, data: item.subsections[2].items },
        { type: 3, data: item.subsections[3].items }
      ]
    }
    educationLevels.value =
      item.educationLevel?.setup?.map((item: LowerItem) => {
        return {
          id: item.id ?? '',
          name: item.text ?? ''
        }
      }) ?? []

    educationLevel.value =
      item.educationLevel?.selected != undefined
        ? ({
            id: item.educationLevel.selected?.id,
            name: item.educationLevel.selected?.text
          } as SelectItem)
        : undefined

    const managementItem = item?.managementExperience?.item

    if (managementItem != undefined) {
      managementExperience.value = [
        {
          id: 0,
          title:
            item?.managementExperience?.item?.yearsOfExperience +
            ' ' +
            localizationStore.translate('profile_ManagementExperience_Years_Of_Experience'),
          description:
            localizationStore.translate('profile_ManagementExperience_Employees_Text') +
            ' ' +
            item.managementExperience?.item?.employees?.text +
            (item.managementExperience?.item?.managedManagers === true
              ? ' ' +
                localizationStore.translate('profile_ManagementExperience_Managed_Managers_Text')
              : ''),
          hide: false,
          dropdownItems: [...profileDropdown().slice(2, 4)]
        }
      ]
    }

    experienceInitialized.value = true
  }

  const setExperiences = (items: Experience) => {
    experiences.value = [
      { type: 0, data: items.workExperiences },
      { type: 1, data: items.educations },
      { type: 2, data: items.courses },
      { type: 3, data: items.volunteerExperiences }
    ]
  }

  const setExperienceInitialized = (initialized: boolean) => {
    experienceInitialized.value = initialized
  }

  return {
    experienceInitialized,
    professionelTitle,
    profileSummary,
    personalProfile,
    experiences,
    educationLevels,
    educationLevel,
    managementExperience,
    managementItem,
    setExperiences,
    initExperience,
    setExperienceInitialized
  }
})
